import { useEffect, useSyncExternalStore } from 'react'
import { estRoleDeSite, type UtilisateurCourant } from '@afs/shared'
import { purgerLesVieuxCoffres } from './depot.js'
import {
  INTERVALLE_SYNCHRO_MS,
  generationCle,
  synchroniser,
  type Agent,
  type ResultatSynchro,
} from './synchro.js'

/**
 * Où en est le coffre de l'agent connecté, pour le dire à l'écran (S314) :
 * `null` tant que rien n'a été tenté. Un seul état pour l'onglet, hors des
 * composants : la coque se remonte à chaque écran.
 */
export type EtatCoffre =
  | (Exclude<ResultatSynchro, { etat: 'limite' }> & { readonly email: string })
  | (Extract<ResultatSynchro, { etat: 'limite' }> & {
      readonly email: string
      /** L'instant du seul nouvel essai programmé. */
      readonly reessaiA: number
    })
  | { readonly etat: 'en-cours'; readonly email: string }

let etat: EtatCoffre | null = null
let reessai: ReturnType<typeof setTimeout> | null = null
/**
 * Le dernier coffre rangé ou trouvé frais, pour qui et sous quelle clé : la
 * coque se remonte à chaque écran. Un coffre trouvé frais part de l'instant de
 * la vérification, pas de son scellement : il peut donc avoir près d'une heure
 * avant d'être retéléchargé, bien en deçà des 7 jours de D5.
 */
let derniereReussite: {
  readonly email: string
  readonly a: number
  readonly generation: number
} | null = null
let purgeFaite = false
const ecouteurs = new Set<() => void>()

function changer(suivant: EtatCoffre | null): void {
  etat = suivant
  for (const ecouteur of ecouteurs) ecouteur()
}

function abonner(ecouteur: () => void): () => void {
  ecouteurs.add(ecouteur)
  return () => {
    ecouteurs.delete(ecouteur)
  }
}

export function etatCoffre(): EtatCoffre | null {
  return etat
}

function annulerReessai(): void {
  if (reessai !== null) clearTimeout(reessai)
  reessai = null
}

/**
 * Une synchronisation, une seule à la fois. Après un 429, UN nouvel essai est
 * programmé à l'échéance de `Retry-After` ; d'ici là, ni la minuterie ni la
 * coque ne relancent rien : pas de boucle contre le plafond.
 */
export async function lancerSynchro(
  agent: Agent,
  options: { readonly forcer?: boolean } = {},
): Promise<void> {
  const email = agent.email
  const recente =
    derniereReussite?.email === email &&
    derniereReussite.generation === generationCle() &&
    Date.now() - derniereReussite.a < INTERVALLE_SYNCHRO_MS
  if (recente && options.forcer !== true) return
  if (etat?.email === email) {
    if (etat.etat === 'en-cours') return
    if (etat.etat === 'limite' && Date.now() < etat.reessaiA) return
  }
  annulerReessai()
  changer({ etat: 'en-cours', email })
  const generation = generationCle()
  const resultat = await synchroniser(agent, options)
  // L'agent a pu se déconnecter pendant l'appel : son résultat ne vaut plus pour l'écran.
  if (etatCoffre()?.email !== email) return
  if (resultat.etat === 'rangee' || resultat.etat === 'fraiche') {
    derniereReussite = { email, a: Date.now(), generation }
  }
  if (resultat.etat !== 'limite') {
    changer({ ...resultat, email })
    return
  }
  const reessaiA = Date.now() + resultat.reessayerDansMs
  changer({ ...resultat, email, reessaiA })
  reessai = setTimeout(() => {
    reessai = null
    void lancerSynchro(agent, options)
  }, resultat.reessayerDansMs)
}

/**
 * À toute fermeture de session (déconnexion, mot de passe changé) : l'agent
 * suivant du poste repart de zéro (S314, S396). Appelée par `useFermerSession`.
 */
export function oublierSynchro(): void {
  annulerReessai()
  derniereReussite = null
  changer(null)
}

/**
 * Mode dégradé (CP-12) — tant que la session est ouverte et le réseau là, le
 * coffre du poste se rafraîchit : à l'ouverture, puis toutes les trente
 * minutes. Seulement pour un poste de site qui vérifie une éligibilité — pas
 * pour la chargée de clientèle, qui la lit en ligne. Un coffre encore frais ne
 * se retélécharge pas (`synchroniser`) : la coque peut demander à chaque écran.
 */
export function useSynchroHorsLigne(utilisateur: UtilisateurCourant | undefined): void {
  const concerne = concerneParLeCoffre(utilisateur)
  const email = utilisateur?.email
  const nomAffiche = utilisateur === undefined ? '' : agentDe(utilisateur).nomAffiche
  const siteNom = utilisateur?.siteNom ?? null

  useEffect(() => {
    if (!concerne || email === undefined) return undefined
    if (!purgeFaite) {
      purgeFaite = true
      void purgerLesVieuxCoffres()
    }
    const tenter = (): void => {
      void lancerSynchro({ email, nomAffiche, siteNom })
    }
    tenter()
    const minuterie = setInterval(tenter, INTERVALLE_SYNCHRO_MS)
    return () => {
      clearInterval(minuterie)
    }
  }, [concerne, email, nomAffiche, siteNom])
}

/**
 * Un compte à second facteur n'a pas de clé (la connexion ne la dérive pas :
 * il ne se déverrouille pas hors ligne), donc jamais de coffre : il n'est pas
 * concerné, et aucun bandeau ne lui demande de se reconnecter en vain (S396).
 */
export function concerneParLeCoffre(
  utilisateur: UtilisateurCourant | undefined,
): utilisateur is UtilisateurCourant {
  return (
    utilisateur !== undefined &&
    !utilisateur.totpActive &&
    estRoleDeSite(utilisateur.role) &&
    utilisateur.permissions.includes('eligibilite:check')
  )
}

/** L'agent tel que le coffre le porte. */
export function agentDe(utilisateur: UtilisateurCourant): Agent {
  return {
    email: utilisateur.email,
    nomAffiche: `${utilisateur.prenom} ${utilisateur.nom}`,
    siteNom: utilisateur.siteNom ?? null,
  }
}

export function useEtatCoffre(): EtatCoffre | null {
  return useSyncExternalStore(abonner, etatCoffre)
}
